// macadamia-app/src/macadamia-core/contexts/ImageCacheContext.tsx
import React, { createContext, useContext, useCallback, ReactNode } from 'react';

interface ImageCacheEntry {
  url: string;
  timestamp: number;
}

interface ImageCacheContextType {
  getImageUrl: (imageId: string) => Promise<string | null>;
  clearCache: () => void;
  clearCacheEntry: (imageId: string) => void;
}

// Cache en mémoire (survit aux remontages de composants)
const imageCache: Record<string, ImageCacheEntry> = {};

// TTL de 50 minutes (marge de sécurité avant expiration S3 à 1h)
const CACHE_TTL = 50 * 60 * 1000;

export const ImageCacheContext = createContext<ImageCacheContextType | undefined>(undefined);

export const ImageCacheProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const getImageUrl = useCallback(async (imageId: string): Promise<string | null> => {
    const now = Date.now();

    // Vérifier le cache en premier
    if (imageCache[imageId] && now - imageCache[imageId].timestamp < CACHE_TTL) {
      return imageCache[imageId].url;
    }


    try {
      const response = await fetch(
        `${process.env.REACT_APP_URL_API_MACADAMIA}/images/${imageId}/url`,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();

      // Stocker dans le cache
      imageCache[imageId] = {
        url: data.imageUrl,
        timestamp: now,
      };


      return data.imageUrl;
    } catch (error) {
      console.error(`[Image Cache] Error fetching image ${imageId}:`, error);
      return null;
    }
  }, []);

  const clearCache = useCallback(() => {
    Object.keys(imageCache).forEach((key) => delete imageCache[key]);
  }, []);

  const clearCacheEntry = useCallback((imageId: string) => {
    delete imageCache[imageId];
  }, []);

  return (
    <ImageCacheContext.Provider value={{ getImageUrl, clearCache, clearCacheEntry }}>
      {children}
    </ImageCacheContext.Provider>
  );
};

export const useImageCache = () => {
  const context = useContext(ImageCacheContext);
  if (!context) {
    throw new Error('useImageCache must be used within ImageCacheProvider');
  }
  return context;
};
